"use client";

import Image from "next/image";
import * as React from "react";

import { cn } from "@/lib/utils";

import type {
  NavMegaMenuGroup,
  NavMegaMenuPreviewKey,
} from "./nav-mega-menu-data";
import { NAV_PREVIEW_IMAGES } from "./nav-mega-menu-data";
import {
  isReactNavPreview,
  NavMegaMenuPreview,
} from "./nav-mega-menu-previews";
import { NavigationDropdownItem } from "./navigation-dropdown-item";

function NavigationDropdownPreview({
  previewKey,
  className,
}: {
  previewKey: NavMegaMenuPreviewKey;
  className?: string;
}) {
  if (isReactNavPreview(previewKey)) {
    return (
      <div className={cn("shrink-0", className)}>
        <NavMegaMenuPreview previewKey={previewKey} />
      </div>
    );
  }

  return (
    <div
      className={cn(
        "rounded-ds-m border-grey-100 relative h-[272px] w-[400px] shrink-0 overflow-hidden border",
        className
      )}
    >
      <Image
        src={NAV_PREVIEW_IMAGES[previewKey]}
        alt=""
        fill
        className="object-cover object-left-top"
        sizes="400px"
        priority
      />
    </div>
  );
}

export function NavigationDropdown({
  menu,
  className,
  defaultActiveIndex = 0,
}: {
  menu: NavMegaMenuGroup;
  className?: string;
  defaultActiveIndex?: number;
}) {
  const [activeIndex, setActiveIndex] = React.useState(defaultActiveIndex);
  const activeItem = menu.items[activeIndex] ?? menu.items[0];
  const previewContentRef = React.useRef<HTMLDivElement>(null);
  const [previewHeight, setPreviewHeight] = React.useState(272);

  React.useLayoutEffect(() => {
    const node = previewContentRef.current;
    if (!node) return;

    const updateHeight = () => {
      setPreviewHeight(node.offsetHeight);
    };

    updateHeight();

    const observer = new ResizeObserver(updateHeight);
    observer.observe(node);
    return () => observer.disconnect();
  }, [activeItem?.previewKey]);

  if (!activeItem) return null;

  return (
    <div
      className={cn(
        "rounded-ds-xl border-grey-100 bg-grey-0 flex items-center gap-12 border py-3 pr-3 pl-12 shadow-[0px_9px_21px_0px_rgba(0,0,0,0.1)]",
        className
      )}
    >
      {/* Figma Dropdown Items (5584:27230): vertically centered in 272px preview row. */}
      <div className="flex w-[307px] shrink-0 flex-col gap-2">
        {menu.items.map((item, index) => (
          <NavigationDropdownItem
            key={item.title}
            item={item}
            isActive={index === activeIndex}
            onHover={() => setActiveIndex(index)}
            onFocus={() => setActiveIndex(index)}
          />
        ))}
      </div>
      <div
        className="shrink-0 overflow-hidden motion-safe:transition-[height] motion-safe:duration-300 motion-safe:ease-[cubic-bezier(0.2,0.8,0.2,1)]"
        style={{ height: previewHeight }}
      >
        <div ref={previewContentRef}>
          <NavigationDropdownPreview previewKey={activeItem.previewKey} />
        </div>
      </div>
    </div>
  );
}
